import { createOperationModel } from '@y42/datasource/shared/operation-input';
import { AggregationType, ColumnTypeEnum, OperationModel, OperatorEnum, OperatorTypeEnum } from '@y42/json-schema';
import { assertTuple } from '@y42/util/assert-tuple';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {
  addDays,
  endOfDay,
  endOfHour,
  endOfMinute,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getDay,
  getDaysInMonth,
  getWeekOfMonth,
  startOfDay,
  startOfHour,
  startOfISOWeekYear,
  startOfMinute,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { BackendValue } from '../widget-data.model';

export interface DataToFilter {
  key: string;
  value: BackendValue;
  agg?: AggregationType;
}

export type DateAggregationType =
  | AggregationType.DayMonthYear
  | AggregationType.WeekMonth
  | AggregationType.WeekMonthYear
  | AggregationType.WeekNumberMonth
  | AggregationType.QuarterYear
  | AggregationType.MonthYear
  | AggregationType.WeekNumberMonthYear
  | AggregationType.WeekNumberIsoYear
  | AggregationType.DateHour
  | AggregationType.DateMinute
  | AggregationType.Year
  | AggregationType.Quarter
  | AggregationType.Month
  | AggregationType.Week
  | AggregationType.WeekNumber
  | AggregationType.Day
  | AggregationType.Hour;

export type DateColumnType =
  | ColumnTypeEnum.Date
  | ColumnTypeEnum.Datetime
  | ColumnTypeEnum.Time
  | ColumnTypeEnum.Timestamp;

export const formatDateSchema: Record<DateColumnType, string> = {
  [ColumnTypeEnum.Date]: 'yyyyMMdd',
  [ColumnTypeEnum.Datetime]: 'yyyyMMddHHmm',
  [ColumnTypeEnum.Time]: 'yyyyMMddHHmm',
  [ColumnTypeEnum.Timestamp]: 'yyyyMMddHHmm',
};

export const formatDate: Record<
  DateAggregationType,
  (input: string, year?: number, month?: number, day?: number) => [string, string]
> = {
  [AggregationType.Year]: (y: string) => {
    const year = Number(y) || 0;
    const dateWithYear = new Date(year, 1, 1);
    const endYear = endOfYear(dateWithYear).toISOString();
    const startYear = startOfYear(dateWithYear).toISOString();
    return [startYear, endYear];
  },
  [AggregationType.Month]: (m: string, y?: number) => {
    const month = Number(m) || 0;
    const dateWithYearMonth = new Date(y || 0, month - 1, 1);
    const endMonth = endOfMonth(dateWithYearMonth).toISOString();
    const startMonth = startOfMonth(dateWithYearMonth).toISOString();
    return [startMonth, endMonth];
  },
  [AggregationType.MonthYear]: (yearMonth: string) => {
    const [y, m] = assertTuple(splitNumbers(yearMonth), 2);
    const dateWithYearMonth = new Date(y, m - 1, 1);
    const endMonth = endOfMonth(dateWithYearMonth).toISOString();
    const startMonth = startOfMonth(dateWithYearMonth).toISOString();
    return [startMonth, endMonth];
  },
  [AggregationType.DayMonthYear]: (yearMonthDay: string) => {
    const [y, m, d] = assertTuple(splitNumbers(yearMonthDay), 3);
    const date = new Date(y, m - 1, d);
    const endDay = endOfDay(date).toISOString();
    const startDay = startOfDay(date).toISOString();
    return [startDay, endDay];
  },
  [AggregationType.Day]: (d: string, y?: number, m?: number) => {
    const day = Number(d) || 0;
    const month = m || 0;
    const dateWithYearMonthDay = new Date(y || 0, month - 1, day);
    const endDay = endOfDay(dateWithYearMonthDay).toISOString();
    const startDay = startOfDay(dateWithYearMonthDay).toISOString();
    return [startDay, endDay];
  },
  [AggregationType.DateHour]: (dateString: string) => {
    const [date, time] = assertTuple(dateString.split(' '), 2);
    const [year, month, day] = assertTuple(date.split('-'), 3);
    const [hours, minutes] = time.split(':');
    const YearMonthDayHourMin = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours ? Number(hours) : 0,
      minutes ? Number(minutes) : 0,
    );
    const endHour = endOfHour(YearMonthDayHourMin).toISOString();
    const startHour = startOfHour(YearMonthDayHourMin).toISOString();
    return [startHour, endHour];
  },
  [AggregationType.DateMinute]: (dateString: string) => {
    const [date, time] = assertTuple(dateString.split(' '), 2);
    const [year, month, day] = assertTuple(date.split('-'), 3);
    const [hours, minutes] = time.split(':');
    const YearMonthDayHourMin = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours ? Number(hours) : 0,
      minutes ? Number(minutes) : 0,
    );
    const endMin = endOfMinute(YearMonthDayHourMin).toISOString();
    const startMin = startOfMinute(YearMonthDayHourMin).toISOString();
    return [startMin, endMin];
  },
  [AggregationType.Hour]: (h: string, y?: number, m?: number, d?: number) => {
    const hour = Number(h);
    const month = m || 0;
    const dateWithYearMonthDayHour = new Date(y || 0, month - 1, d, hour);
    const endHour = endOfHour(dateWithYearMonthDayHour).toISOString();
    const startHour = startOfHour(dateWithYearMonthDayHour).toISOString();
    return [startHour, endHour];
  },
  [AggregationType.Week]: (w: string, y?: number, m?: number, d?: number) => {
    const month = m || 0;
    const firstDayOfWeekWithYearMonthDay = new Date(y || 0, month - 1, d);
    const lastDayOfWeeekWithYearMonthDay = addDays(firstDayOfWeekWithYearMonthDay, 7);
    const endWeek = endOfDay(lastDayOfWeeekWithYearMonthDay).toISOString();
    const startWeek = startOfDay(firstDayOfWeekWithYearMonthDay).toISOString();
    return [startWeek, endWeek];
  },
  [AggregationType.WeekMonth]: (monthWeekDay: string, y?: number, m?: number, d?: number) => {
    const dateWithYearMonthDay = new Date(y || 0, m || 0, d);
    const endDay = endOfDay(dateWithYearMonthDay).toISOString();
    const startDay = startOfDay(dateWithYearMonthDay).toISOString();
    return [startDay, endDay];
  },
  [AggregationType.WeekMonthYear]: (monthWeekDay: string, y?: number, m?: number, d?: number) => {
    const month = m || 0;
    const dateWithYearMonthDay = new Date(y || 0, month - 1, d);
    const endDay = endOfDay(dateWithYearMonthDay).toISOString();
    const startDay = startOfDay(dateWithYearMonthDay).toISOString();
    return [startDay, endDay];
  },
  [AggregationType.Quarter]: (q: string, y?: number) => {
    const quarter = Number(q) || 0;
    const dateWithYearMonth = new Date(y || 0, quarter * 3 - 1);
    const endQuarter = endOfQuarter(dateWithYearMonth).toISOString();
    const startQuarter = startOfQuarter(dateWithYearMonth).toISOString();
    return [startQuarter, endQuarter];
  },
  [AggregationType.QuarterYear]: (yearQuarter: string) => {
    const [year, quarter] = assertTuple(splitNumbers(yearQuarter), 2);
    const dateWithYearMonth = new Date(year || 0, quarter * 3 - 1);
    const endQuarter = endOfQuarter(dateWithYearMonth).toISOString();
    const startQuarter = startOfQuarter(dateWithYearMonth).toISOString();
    return [startQuarter, endQuarter];
  },
  [AggregationType.WeekNumber]: (yearMonthDay: string) => {
    const [year, month, day] = assertTuple(splitNumbers(yearMonthDay), 3);
    const startOfIsoNthWeek = new Date(year, month - 1, day);
    const endOfIsoWeek = endOfDay(addDays(startOfIsoNthWeek, 6)).toISOString();
    const startOfIsoWeek = startOfDay(startOfIsoNthWeek).toISOString();
    return [startOfIsoWeek, endOfIsoWeek];
  },
  [AggregationType.WeekNumberMonth]: (yearMonthDay: string) => {
    const [year, month, day] = assertTuple(splitNumbers(yearMonthDay), 3);
    const startOfIsoNthWeek = new Date(year, month - 1, day);
    const endOfIsoWeek = endOfDay(addDays(startOfIsoNthWeek, 6)).toISOString();
    const startOfIsoWeek = startOfDay(startOfIsoNthWeek).toISOString();
    return [startOfIsoWeek, endOfIsoWeek];
  },
  // same as AggregationType.WeekNumberIsoYear, but this one is to be removed
  [AggregationType.WeekNumberMonthYear]: (yearMonthDay: string) => {
    const [year, month, day] = assertTuple(splitNumbers(yearMonthDay), 3);
    const startOfIsoNthWeek = new Date(year, month - 1, day);
    const endOfIsoWeek = endOfDay(addDays(startOfIsoNthWeek, 6)).toISOString();
    const startOfIsoWeek = startOfDay(startOfIsoNthWeek).toISOString();
    return [startOfIsoWeek, endOfIsoWeek];
  },
  [AggregationType.WeekNumberIsoYear]: (yearMonthDay: string) => {
    const [year, month, day] = assertTuple(splitNumbers(yearMonthDay), 3);
    const startOfIsoNthWeek = new Date(year, month - 1, day);
    const endOfIsoWeek = endOfDay(addDays(startOfIsoNthWeek, 6)).toISOString();
    const startOfIsoWeek = startOfDay(startOfIsoNthWeek).toISOString();
    return [startOfIsoWeek, endOfIsoWeek];
  },
};

export const operationsByAggregation: Record<
  DateAggregationType,
  (agg: DateAggregationType, value: string, yearLimit?: { minYear: number; maxYear: number }) => OperationModel[]
> = {
  [AggregationType.Year]: (agg: DateAggregationType, value: string) => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.Month]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      operations.push(
        createOperationModel(
          null,
          OperatorEnum.Between,
          formatDate[agg](value, yearLimit.minYear),
          OperatorTypeEnum.Absolute,
        ),
      );
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.MonthYear]: (agg: DateAggregationType, value: string) => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.DayMonthYear]: (agg: DateAggregationType, value: string): OperationModel[] => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.Day]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    const endMonth = 12;
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      let startMonth = 1;
      while (startMonth <= endMonth) {
        operations.push(
          createOperationModel(
            null,
            OperatorEnum.Between,
            formatDate[agg](value, yearLimit.minYear, startMonth),
            OperatorTypeEnum.Absolute,
          ),
        );
        startMonth++;
      }
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.DateHour]: (agg: DateAggregationType, value: string) => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.DateMinute]: (agg: DateAggregationType, value: string) => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.Hour]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    const endMonth = 12;
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      let startMonth = 1;
      while (startMonth <= endMonth) {
        let startDay = 1;
        const endDayOfMonth = getDaysInMonth(new Date(yearLimit.minYear, startMonth));
        while (startDay <= endDayOfMonth) {
          operations.push(
            createOperationModel(
              null,
              OperatorEnum.Between,
              formatDate[agg](value, yearLimit.minYear, startMonth, startDay),
              OperatorTypeEnum.Absolute,
            ),
          );
          startDay++;
        }
        startMonth++;
      }
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.Week]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    const endMonth = 12;
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      let startMonth = 1;
      while (startMonth <= endMonth) {
        let startDay = 1;
        const endDayOfMonth = getDaysInMonth(new Date(yearLimit.minYear, startMonth));
        while (startDay <= endDayOfMonth) {
          if (getWeekOfMonth(new Date(yearLimit.minYear, startMonth, startDay)) === Number(value)) {
            operations.push(
              createOperationModel(
                null,
                OperatorEnum.Between,
                formatDate[agg](value, yearLimit.minYear, startMonth, startDay),
                OperatorTypeEnum.Absolute,
              ),
            );
            startDay = endDayOfMonth;
          } else {
            startDay++;
          }
        }
        startMonth++;
      }
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.WeekMonth]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      const [month, weekday] = assertTuple(splitNumbers(value), 2);
      let startDay = 1;
      const endDayOfMonth = getDaysInMonth(new Date(yearLimit.minYear, month - 1));
      while (startDay <= endDayOfMonth) {
        if (getDay(new Date(yearLimit.minYear, month - 1, startDay)) === weekday) {
          operations.push(
            createOperationModel(
              null,
              OperatorEnum.Between,
              formatDate[agg](value, yearLimit.minYear, month - 1, startDay),
              OperatorTypeEnum.Absolute,
            ),
          );
        }
        startDay++;
      }
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.WeekMonthYear]: (agg: DateAggregationType, value: string) => {
    const operations = [] as OperationModel[];
    const [year, month, weekday] = assertTuple(splitNumbers(value), 3);
    let startDay = 1;
    const endDayOfMonth = getDaysInMonth(new Date(year, month - 1));
    while (startDay <= endDayOfMonth) {
      if (getDay(new Date(year, month - 1, startDay)) === weekday) {
        operations.push(
          createOperationModel(
            null,
            OperatorEnum.Between,
            formatDate[agg](value, year, month, startDay),
            OperatorTypeEnum.Absolute,
          ),
        );
      }
      startDay++;
    }
    return operations;
  },
  [AggregationType.Quarter]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      operations.push(
        createOperationModel(
          null,
          OperatorEnum.Between,
          formatDate[agg](value, yearLimit.minYear),
          OperatorTypeEnum.Absolute,
        ),
      );
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.QuarterYear]: (agg: DateAggregationType, value: string) => {
    return [createOperationModel(null, OperatorEnum.Between, formatDate[agg](value), OperatorTypeEnum.Absolute)];
  },
  [AggregationType.WeekNumber]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      const middleOfYear = new Date(yearLimit.minYear, 6);
      const startOfIsoWeek = startOfISOWeekYear(middleOfYear);
      const weekLength = 7;
      const startOfIsoNthWeek = format(addDays(startOfIsoWeek, (Number(value) - 1) * weekLength), 'yyyy-MM-dd');
      operations.push(
        createOperationModel(null, OperatorEnum.Between, formatDate[agg](startOfIsoNthWeek), OperatorTypeEnum.Absolute),
      );
      yearLimit.minYear++;
    }
    return operations;
  },
  [AggregationType.WeekNumberMonth]: (
    agg: DateAggregationType,
    value: string,
    yearLimit?: { minYear: number; maxYear: number },
  ) => {
    const operations = [] as OperationModel[];
    if (!yearLimit) {
      return operations;
    }
    const [month, isoweekNumber] = splitNumbers(value);
    while (yearLimit.minYear <= yearLimit.maxYear && agg) {
      const middleOfYear = new Date(yearLimit.minYear, 6);
      const startOfIsoWeek = startOfISOWeekYear(middleOfYear);
      const weekLength = 7;
      const startOfIsoNthWeek = format(addDays(startOfIsoWeek, (Number(isoweekNumber) - 1) * weekLength), 'yyyy-MM-dd');
      operations.push(
        createOperationModel(null, OperatorEnum.Between, formatDate[agg](startOfIsoNthWeek), OperatorTypeEnum.Absolute),
      );
      yearLimit.minYear++;
    }
    return operations;
  },
  // same as AggregationType.WeekNumberIsoYear, but this one is to be removed
  [AggregationType.WeekNumberMonthYear]: (agg: DateAggregationType, value: string) => {
    const operations = [] as OperationModel[];
    const [year, month, isoweekNumber] = assertTuple(splitNumbers(value), 3);
    const middleOfYear = new Date(year, 6);
    const startOfIsoWeek = startOfISOWeekYear(middleOfYear);
    const weekLength = 7;
    const startOfIsoNthWeek = format(addDays(startOfIsoWeek, (Number(isoweekNumber) - 1) * weekLength), 'yyyy-MM-dd');

    operations.push(
      createOperationModel(null, OperatorEnum.Between, formatDate[agg](startOfIsoNthWeek), OperatorTypeEnum.Absolute),
    );
    return operations;
  },
  [AggregationType.WeekNumberIsoYear]: (agg: DateAggregationType, value: string) => {
    const operations = [] as OperationModel[];
    const [year, isoweekNumber] = assertTuple(splitNumbers(value), 2);
    const middleOfYear = new Date(year, 6);
    const startOfIsoWeek = startOfISOWeekYear(middleOfYear);
    const weekLength = 7;
    const startOfIsoNthWeek = format(addDays(startOfIsoWeek, (Number(isoweekNumber) - 1) * weekLength), 'yyyy-MM-dd');

    operations.push(
      createOperationModel(null, OperatorEnum.Between, formatDate[agg](startOfIsoNthWeek), OperatorTypeEnum.Absolute),
    );
    return operations;
  },
};

function splitNumbers(str: string): number[] {
  return str.split('-').map((v) => Number.parseInt(v));
}
